import React, { useState } from 'react';
import {
  Bell,
  Send,
  Users,
  Wrench,
  Globe,
  AlertTriangle,
  Info,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import type { AdminBroadcastPayload } from '../../types';

interface AdminBroadcastSectionProps {
  onSendBroadcast: (payload: AdminBroadcastPayload) => Promise<void>;
  isProcessing: boolean;
}

export const AdminBroadcastSection: React.FC<AdminBroadcastSectionProps> = ({
  onSendBroadcast,
  isProcessing,
}) => {
  const [targetAudience, setTargetAudience] = useState<'all' | 'professionals' | 'customers'>('all');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'info' | 'warning' | 'alert'>('info');
  const [sentSuccess, setSentSuccess] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;

    await onSendBroadcast({
      title: title.trim(),
      message: message.trim(),
      targetAudience,
      type,
    });

    setSentSuccess(true);
    setTitle('');
    setMessage('');
    setTimeout(() => setSentSuccess(false), 5000);
  };

  return (
    <div id="admin-broadcast-section" className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">System Broadcast & Alert Dispatcher</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Deliver immediate announcements, scheduled maintenance notifications, or policy alerts directly to in-app user feeds.
            </p>
          </div>
        </div>
      </div>

      {sentSuccess && (
        <div className="p-4 bg-emerald-500/20 border border-emerald-500/40 rounded-2xl flex items-center gap-3 text-emerald-300 text-xs font-semibold animate-fade-in">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span>Broadcast announcement successfully dispatched to target recipient feeds and logged in audit trails.</span>
        </div>
      )}

      {/* Broadcast Composer Form */}
      <form onSubmit={handleSend} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-6">
        {/* Target Audience Selector */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
            Target Audience:
          </label>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setTargetAudience('all')}
              className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                targetAudience === 'all'
                  ? 'bg-emerald-500 text-slate-950 border-emerald-400 shadow-md'
                  : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>All Users</span>
            </button>

            <button
              type="button"
              onClick={() => setTargetAudience('professionals')}
              className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                targetAudience === 'professionals'
                  ? 'bg-emerald-500 text-slate-950 border-emerald-400 shadow-md'
                  : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Wrench className="w-4 h-4" />
              <span>Artisans Only</span>
            </button>

            <button
              type="button"
              onClick={() => setTargetAudience('customers')}
              className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                targetAudience === 'customers'
                  ? 'bg-emerald-500 text-slate-950 border-emerald-400 shadow-md'
                  : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Customers Only</span>
            </button>
          </div>
        </div>

        {/* Severity Type */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
            Notification Level:
          </label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="radio"
                name="type"
                checked={type === 'info'}
                onChange={() => setType('info')}
                className="accent-emerald-500"
              />
              <span className="flex items-center gap-1">
                <Info className="w-3.5 h-3.5 text-blue-400" />
                Information
              </span>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="radio"
                name="type"
                checked={type === 'warning'}
                onChange={() => setType('warning')}
                className="accent-amber-500"
              />
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                Warning / Maintenance
              </span>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="radio"
                name="type"
                checked={type === 'alert'}
                onChange={() => setType('alert')}
                className="accent-rose-500"
              />
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                Critical Alert
              </span>
            </label>
          </div>
        </div>

        {/* Title Input */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
            Broadcast Headline / Title:
          </label>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Scheduled Core Maintenance Window — Tonight at 11:00 PM WAT"
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-4 py-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </div>

        {/* Message Input */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
            Detailed Announcement Body:
          </label>
          <textarea
            required
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Provide complete instructions, timeframes, or policy highlights..."
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 leading-relaxed"
          />
        </div>

        {/* Live Preview Card */}
        {title && (
          <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-2xl space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
              Live Preview
            </span>
            <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <div className="text-xs font-bold text-white flex items-center gap-2">
                {type === 'warning' ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                ) : type === 'alert' ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                ) : (
                  <Info className="w-3.5 h-3.5 text-blue-400" />
                )}
                <span>{title}</span>
              </div>
              <p className="text-[11px] text-slate-300">{message || 'Message preview...'}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isProcessing || !title.trim() || !message.trim()}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-extrabold rounded-2xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            <span>{isProcessing ? 'Transmitting...' : 'Dispatch Broadcast Now'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};
